import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';
import type { EmployeeDetail as EmployeeDetailType } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEmployeesApi, useHrApi } from '@/services/api';
import { EmployeeStatusBadge } from './EmployeeStatusBadge';
import { EmployeeForm, EmployeeFormValues } from './EmployeeForm';
import { formValuesToEmployeeDto, peselWarningMessage } from './employeeFormUtils';
import { StudentCardEditor } from './StudentCardEditor';
import { RateHistoryEditor } from './RateHistoryEditor';
import { EmployeePayrollCard } from './EmployeePayrollCard';
import { MonthlyHoursGrid } from './MonthlyHoursGrid';
import { EmployeeDocuments } from './EmployeeDocuments';
import { t } from '@/i18n';

interface EmployeeDetailProps {
  companyNip: string;
}

export function EmployeeDetail({ companyNip }: EmployeeDetailProps) {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { findOne, update, terminate, archive, unarchive } =
    useEmployeesApi();
  const { unlockMyData } = useHrApi();
  const [employee, setEmployee] = useState<EmployeeDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('identity');

  const refresh = () => {
    if (!employeeId) return;
    let cancelled = false;
    setLoading(true);
    findOne(companyNip, employeeId)
      .then((e) => { if (!cancelled) setEmployee(e); })
      .catch((err) => {
        if (!cancelled) {
          toast.error((err as Error).message);
          navigate(`/${companyNip}/employees`);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };

  useEffect(refresh, [companyNip, employeeId]);

  const handleSave = async (values: EmployeeFormValues) => {
    if (!employee) return;
    setSaving(true);
    try {
      const dto = formValuesToEmployeeDto(values);
      const updated = await update(companyNip, employee.id, dto);
      setEmployee(updated);
      toast.success(t('common.save') + ' ✓');
      const peselWarning = peselWarningMessage(updated);
      if (peselWarning) toast.warning(peselWarning);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleTerminate = async () => {
    if (!employee) return;
    setBusy(true);
    try {
      await terminate(companyNip, employee.id);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const handleArchiveToggle = async () => {
    if (!employee) return;
    setBusy(true);
    try {
      if (employee.archivedAt) await unarchive(companyNip, employee.id);
      else await archive(companyNip, employee.id);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!employee) {
    return loading ? (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-9 w-full max-w-lg" />
        <Skeleton className="h-96 w-full" />
      </div>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold tracking-tight">
          {employee.firstName} {employee.lastName}
        </h1>
        <EmployeeStatusBadge status={employee.status} />
        {employee.myDataLocked && (
          <span className="flex items-center gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs text-rose-700 dark:text-rose-400">
            <ShieldAlert className="size-3.5" />
            {t('employees.myDataLock.badge')}
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={async () => {
                try {
                  await unlockMyData(companyNip, employee.id);
                  toast.success(t('employees.myDataLock.unlocked'));
                  refresh();
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            >
              {t('employees.myDataLock.unlock')}
            </Button>
          </span>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="identity">{t('employees.identity.tab')}</TabsTrigger>
          <TabsTrigger value="employment">{t('employees.employment.tab')}</TabsTrigger>
          <TabsTrigger value="rates">{t('employees.rates.tab')}</TabsTrigger>
          <TabsTrigger value="payroll">{t('employees.payroll.tab')}</TabsTrigger>
          <TabsTrigger value="hours">{t('employees.hours.tab')}</TabsTrigger>
          <TabsTrigger value="documents">{t('employees.documents.tab')}</TabsTrigger>
        </TabsList>

        {/* forceMount + hidden-when-inactive: preserve unsaved form/draft state across tab switches */}
        <TabsContent
          value="identity"
          forceMount
          className="flex flex-col gap-4 pt-2 data-[state=inactive]:hidden"
        >
          <EmployeeForm
            companyNip={companyNip}
            initialValues={employee}
            formId="employee-edit-form"
            onSubmit={handleSave}
            disabled={saving}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" form="employee-edit-form" disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
            {employee.status !== 'terminated' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={busy}>
                    {busy && <Loader2 className="size-4 animate-spin" />}
                    {t('employees.employment.terminate')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('employees.employment.terminateConfirm')}
                    </AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-white hover:bg-destructive/90"
                      onClick={handleTerminate}
                    >
                      {t('common.confirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" onClick={handleArchiveToggle} disabled={busy}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {employee.archivedAt
                ? t('employees.employment.unarchive')
                : t('employees.employment.archive')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="employment" forceMount className="pt-2 data-[state=inactive]:hidden">
          {employee.employmentType === 'mandate_contract_student' ? (
            <StudentCardEditor
              companyNip={companyNip}
              employeeId={employee.id}
              hasCard={employee.hasStudentCard}
              expiryDate={employee.studentCardExpiry}
              onChange={refresh}
            />
          ) : (
            <p className="text-muted-foreground text-sm">
              {t('employees.employment.tab')} —{' '}
              {employee.employmentType
                ? t(`employees.type.${employee.employmentType}`)
                : t('employees.type.notSet')}
            </p>
          )}
        </TabsContent>

        <TabsContent value="rates" className="pt-2">
          <RateHistoryEditor companyNip={companyNip} employeeId={employee.id} />
        </TabsContent>

        <TabsContent value="payroll" className="pt-2">
          <EmployeePayrollCard companyNip={companyNip} employeeId={employee.id} />
        </TabsContent>

        <TabsContent value="hours" className="pt-2">
          <MonthlyHoursGrid
            companyNip={companyNip}
            employeeId={employee.id}
            isHourly={employee.currentRate?.rateType === 'hourly'}
          />
        </TabsContent>

        <TabsContent value="documents" className="pt-2">
          <EmployeeDocuments companyNip={companyNip} employeeId={employee.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
